import { GQL_ID, NestedFilter } from '@@partner:types:graphql'
import React, { useCallback, useMemo } from 'react'
import { Tag, CheckPicker, Loader } from 'rsuite'

import FilterPanel from '@components/common/filter/FilterPanel'
import { createFilter } from '@components/common/filter/FilterConfig'
import { getSearchableTextMaterialStatus } from '@@partner:helpers/textMaterial'
import { useFetchAllTextMaterialTypes } from '@@partner:graphql/query/textMaterial/types/allTextMaterialTypes.gql'
import { useFetchAllTextMaterialCategories } from '@@partner:graphql/query/textMaterial/categories/allTextMaterialCategories.gql'
import { useFetchAllTextMaterialThemes } from '@@partner:graphql/query/textMaterial/themes/allTextMaterialThemes.gql'
import { makeFilter } from '@@partner:helpers/crud/filters'

export interface TextMaterialsFiltersType {
  id?: GQL_ID
  name?: string
  status?: string[]
  type?: GQL_ID[]
  categoryIds?: GQL_ID[]
  themeIds?: GQL_ID[]
  publishedAt?: { from?: number; to?: number }
}

export interface TextMaterialsFilterProps {
  onFilterChanged: (filters: NestedFilter) => void
}

const defaultFilter: TextMaterialsFiltersType = {
  id: undefined,
  name: undefined,
  status: undefined,
  type: undefined,
  categoryIds: undefined,
  themeIds: undefined,
  publishedAt: undefined
}

const AVAILABLE_STATUSES = getSearchableTextMaterialStatus()

export const prepareTextMaterialsFilter = (filters: TextMaterialsFiltersType): NestedFilter => {
  const idFilter = makeFilter('id', 'eq', filters.id)
  const nameFilter = makeFilter('name', 'includes', filters.name)
  const statusFilter = makeFilter('status', 'contains', filters.status)
  const typeFilter = makeFilter('type', 'contains', filters.type)
  const categoryIdsFilter = makeFilter('categoryIds', 'contains', filters.categoryIds)
  const themeIdsFilter = makeFilter('themeIds', 'contains', filters.themeIds)

  const conditions: NestedFilter[] = [
    idFilter,
    nameFilter,
    statusFilter,
    typeFilter,
    categoryIdsFilter,
    themeIdsFilter
  ]

  // Добавляем фильтр по дате публикации если указан
  if (filters.publishedAt?.from || filters.publishedAt?.to) {
    const fromTs = filters.publishedAt.from ?? 0
    const toTs = filters.publishedAt.to ?? Date.now()
    conditions.push({
      fields: [ 'published_at' ],
      op: 'between',
      value: [ fromTs, toTs ]
    })
  }

  return { and: conditions }
}

// Компонент пикера типов
function TypesPicker ({ value, onChange }: { value: GQL_ID[]; onChange: (value: GQL_ID[]) => void }) {
  const { data, loading } = useFetchAllTextMaterialTypes()
  
  const options = useMemo(() => 
    data?.allTextMaterialTypes?.map(type => ({
      value: type.id,
      label: type.name
    })) ?? []
  , [ data ])

  return (
    <CheckPicker
      value={value ?? []}
      data={options}
      placeholder="Тип материала"
      searchable={false}
      size="sm"
      block
      onChange={onChange}
      renderMenu={menu => loading ? <Loader center content="Загрузка..." /> : menu}
    />
  )
}

// Компонент пикера категорий
function CategoriesPicker ({ value, onChange }: { value: GQL_ID[]; onChange: (value: GQL_ID[]) => void }) {
  const { data, loading } = useFetchAllTextMaterialCategories()
  
  const options = useMemo(() => 
    data?.allTextMaterialCategories?.map(cat => ({
      value: cat.id,
      label: cat.name
    })) ?? []
  , [ data ])

  return (
    <CheckPicker
      value={value ?? []}
      data={options}
      placeholder="Категория"
      searchable={false}
      size="sm"
      block
      onChange={onChange}
      renderMenu={menu => loading ? <Loader center content="Загрузка..." /> : menu}
    />
  )
}

// Компонент пикера тем
function ThemesPicker ({ value, onChange }: { value: GQL_ID[]; onChange: (value: GQL_ID[]) => void }) {
  const { data, loading } = useFetchAllTextMaterialThemes({
    pager: { page: 1, limit: 100 },
    sort: { fieldName: 'id', direction: 0 }
  })
  
  const options = useMemo(() => 
    data?.allTextMaterialThemes?.edges?.map(theme => ({
      value: theme.id,
      label: theme.name
    })) ?? []
  , [ data ])

  return (
    <CheckPicker
      value={value ?? []}
      data={options}
      placeholder="Тема"
      searchable={false}
      size="sm"
      block
      onChange={onChange}
      renderMenu={menu => loading ? <Loader center content="Загрузка..." /> : menu}
    />
  )
}

const filterConfig = [
  createFilter.text('id', 'ID'),
  createFilter.text('name', 'Название'),
  createFilter.multiselect('status', AVAILABLE_STATUSES, 'Статус', {
    searchable: false,
    renderMenuItem: (label, item) => (
      <Tag key={item?.value} color={item?.color as any}>{label}</Tag>
    )
  }),
  createFilter.custom('type', 'Тип', ({ value, onChange }) => (
    <TypesPicker value={value ?? []} onChange={onChange} />
  ), { valueType: 'string[]' }),
  createFilter.custom('categoryIds', 'Категория', ({ value, onChange }) => (
    <CategoriesPicker value={value ?? []} onChange={onChange} />
  ), { valueType: 'string[]' }),
  createFilter.custom('themeIds', 'Тема', ({ value, onChange }) => (
    <ThemesPicker value={value ?? []} onChange={onChange} />
  ), { valueType: 'string[]' }),
  createFilter.dateRange('publishedAt', 'Дата публикации', { expandable: true, section: 'Даты' })
]

export default function TextMaterialsFilter ({
  onFilterChanged
}: TextMaterialsFilterProps) {
  const handleSubmit = useCallback((state: TextMaterialsFiltersType) => {
    onFilterChanged(prepareTextMaterialsFilter(state))
  }, [ onFilterChanged ])

  return (
    <FilterPanel
      filters={filterConfig}
      defaultState={defaultFilter}
      onSearch={handleSubmit}
    />
  )
}
